import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { quillEditorStandardProps, quillFormItemProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { storageFileToRcFile } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row, Select, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import {
  useAddDashboardNoticeAttachments,
  useCreateDashboardNotice,
  useDeleteDashboardNoticeAttachment,
  useUpdateDashboardNotice,
} from "../../../../common/api/queries";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import {
  DashboardNoticePosition,
  dashboardNoticePositionTMap,
  DashboardNoticeType,
  dashboardNoticeTypeTMap,
} from "../../enums";
import { CreateUpdateDashboardNotice, DashboardNotice } from "../../types";
import { convertDashboardNoticeToCreateUpdateObject } from "../../utils";
import { DashboardNoticePositionTag, DashboardNoticeTypeTag } from "../widgets/notices/components";

interface Props {
  open: boolean;
  notice?: DashboardNotice;
  onFormCancel: VoidFunction;
}

export const DashboardNoticeForm = ({ open, notice, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateDashboardNotice & { files?: RcFile[] }>();

  const createDashboardNotice = useCreateDashboardNotice();
  const updateDashboardNotice = useUpdateDashboardNotice();
  const addDashboardNoticeAttachments = useAddDashboardNoticeAttachments();
  const deleteDashboardNoticeAttachment = useDeleteDashboardNoticeAttachment();

  useEffect(() => {
    if (open && notice) {
      const formData = convertDashboardNoticeToCreateUpdateObject(notice);

      form.setFieldsValue({
        ...formData,
        files: notice.attachments.map((attachment) => storageFileToRcFile(attachment.file)),
      });
    }
  }, [open, notice]);

  const updateFiles = (files: RcFile[], id: UUID) => {
    const formData = new FormData();

    if (!notice) {
      files?.forEach((file) => {
        formData.append("files", file);
      });

      addDashboardNoticeAttachments.mutate({ id, object: formData });
      return;
    }

    const filesToRemove = notice.attachments.filter(
      (attachment) => !files.find((file) => file.uid === attachment.file.id),
    );
    const filesToAdd = files.filter(
      (file) => !notice.attachments.find((attachment) => file.uid === attachment.file.id),
    );

    filesToRemove.forEach((file) => {
      deleteDashboardNoticeAttachment.mutate({ id1: id, id2: file.id });
    });

    if (filesToAdd.length) {
      filesToAdd.forEach((file) => {
        formData.append("files", file);
      });

      addDashboardNoticeAttachments.mutate({ id, object: formData });
    }
  };

  const handleFormSubmit = () => {
    form.validateFields().then(async (values) => {
      if (notice) {
        const updatedDashboardNotice = await updateDashboardNotice.mutateAsync(
          { id: notice.id, object: values },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "dashboard.notices.attrs", error.violations),
          },
        );

        if (values.files?.length) {
          updateFiles(values.files, updatedDashboardNotice.id);
        }
      } else {
        const createdDashboardNotice = await createDashboardNotice.mutateAsync(values, {
          onSuccess: onFormCancel,
          onError: (error) => setErrorsToForm(form, "dashboard.notices.attrs", error.violations),
        });

        if (values.files?.length) {
          updateFiles(values.files, createdDashboardNotice.id);
        }
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={open}
      title={t(`dashboard.notices.actions.${notice ? "update" : "create"}`)}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createDashboardNotice.isPending || updateDashboardNotice.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      afterClose={form.resetFields}
      mask={{ closable: false }}
      destroyOnHidden
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={notice} />

      <Form form={form} layout="vertical" name="createUpdateDashboardNotice">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="title"
              label={t("dashboard.notices.attrs.title")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="type" label={t("dashboard.notices.enums.type._label")} rules={[validations.notNull]}>
              <Select
                {...selectTagsStandardProps(dashboardNoticeTypeTMap)}
                options={Object.values(DashboardNoticeType).map((type) => ({
                  value: type,
                  label: <DashboardNoticeTypeTag type={type} />,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="text"
              label={t("dashboard.notices.attrs.text")}
              rules={[validations.notBlank, validations.size(1, 8192)]}
              {...quillFormItemProps}
            >
              <ReactQuill {...quillEditorStandardProps} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="positions"
              label={t("dashboard.notices.enums.position._label")}
              rules={[validations.none]}
              initialValue={[]}
            >
              <Select
                {...selectTagsStandardProps(dashboardNoticePositionTMap)}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                tagRender={(props) => (
                  <DashboardNoticePositionTag
                    position={props.value}
                    closable={props.closable}
                    onClose={props.onClose}
                    label={props.label}
                  />
                )}
                options={Object.values(DashboardNoticePosition).map((position) => ({
                  value: position,
                  label: <DashboardNoticePositionTag position={position} />,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="closed"
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.notNull]}
              initialValue={false}
            >
              <Checkbox>{t("dashboard.notices.attrs.closed")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="files"
          getValueFromEvent={({ fileList }) =>
            fileList.map((item: UploadFile | File) => ("originFileObj" in item ? item.originFileObj : item))
          }
          rules={[validations.none]}
          valuePropName="fileList"
        >
          <Upload multiple beforeUpload={() => false}>
            <ActionButton icon="plus">{t("common.addAttachments")}</ActionButton>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
